"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import {
  formatNumberList,
  formatPercent,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type GrowthReport,
  growthReport,
  type MeansReport,
  meansReport,
} from "@/lib/tool-kit/stats-descriptive/means";

/** English strings. A translated content file overrides these through `ui`. */
export const GEOMETRIC_MEAN_CALCULATOR_STRINGS = {
  modeLabel: "What are you averaging?",
  modeValues: "Positive values",
  modeGrowth: "Percentage changes (growth rates)",
  valuesLabel:
    "Your values — positive numbers separated by commas, spaces, or new lines",
  valuesPlaceholder: "12, 18, 30, 45, 200",
  changesLabel:
    "Percentage change in each period — for example 10, -5, 20 for +10%, −5%, +20%",
  changesPlaceholder: "10, -5, 20",
  valuesDetected: "one: value detected|other: values detected",
  tryExample: "Try an example",
  exampleValues: "12, 18, 30, 45, 200",
  exampleChanges: "10, -5, 20",
  clear: "Clear",
  enterValues: "Enter at least two positive numbers.",
  nonPositive:
    "one: The geometric and harmonic means need every value to be greater than zero — {count} of yours is not.|other: The geometric and harmonic means need every value to be greater than zero — {count} of yours are not.",
  enterChanges: "Enter at least one percentage change.",
  tooNegative:
    "A change of −100% or worse wipes the quantity out, so no average growth rate exists.",
  results: "Results",
  geometric: "Geometric mean",
  harmonic: "Harmonic mean",
  arithmetic: "Arithmetic mean, for comparison",
  count: "Values (n)",
  averageRate: "Average growth per period",
  simpleAverage: "Simple average of the changes",
  totalChange: "Total change over all periods",
  periods: "Periods (n)",
  workingTitle: "How it was calculated",
  logLine: "ln GM = (Σ ln x) ÷ n = {sumLog} ÷ {n} = {meanLog}",
  expLine: "GM = exp({meanLog}) = {gm}",
  productLine: "Check: ({factors})^(1/{n}) = ({product})^(1/{n}) = {gm}",
  harmonicLine: "HM = n ÷ Σ(1/x) = {n} ÷ {sumReciprocal} = {hm}",
  orderNote:
    "For positive data the harmonic mean ≤ geometric mean ≤ arithmetic mean, with equality only when every value is the same.",
  factorsLine: "Growth factors (1 + change ÷ 100): {list}",
  productFactorsLine:
    "Product of the factors = {product}, a total change of {total}",
  averageLine:
    "Average factor = ({product})^(1/{n}) = {factor} → average growth = {rate} per period",
  checkLine:
    "Check: {factor}^{n} = {product} — the same total as the actual changes",
  growthNote:
    "The simple average of the changes overstates typical growth: +50% then −50% averages 0%, yet leaves you with 75% of the starting amount.",
  listSeparator: ", ",
  moreValues: "… and {count} more",
  times: " × ",
} as const;

type Strings = typeof GEOMETRIC_MEAN_CALCULATOR_STRINGS;
type Mode = "values" | "growth";

/** Show the product form only while it stays readable. */
const PRODUCT_LIMIT = 8;

function ValuesResult({
  report,
  values,
  fmt,
  strings,
}: {
  report: MeansReport;
  values: number[];
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const num = (value: number) => formatStat(fmt, value);
  let product = 1;
  for (const value of values) {
    product *= value;
  }
  const showProduct =
    values.length <= PRODUCT_LIMIT &&
    Number.isFinite(product) &&
    product < 1e15;
  return (
    <>
      <div aria-live="polite">
        <StatGrid>
          <StatCard
            emphasis
            label={strings.geometric}
            value={num(report.geometric)}
          />
          <StatCard label={strings.harmonic} value={num(report.harmonic)} />
          <StatCard label={strings.arithmetic} value={num(report.arithmetic)} />
          <StatCard label={strings.count} value={fmt(report.n)} />
        </StatGrid>
      </div>
      <WorkingPanel title={strings.workingTitle}>
        <Formula>
          {t(strings.logLine, {
            sumLog: num(report.sumLog),
            n: fmt(report.n),
            meanLog: num(report.meanLog),
          })}
        </Formula>
        <Formula>
          {t(strings.expLine, {
            meanLog: num(report.meanLog),
            gm: num(report.geometric),
          })}
        </Formula>
        {showProduct ? (
          <Formula>
            {t(strings.productLine, {
              factors: values.map(num).join(strings.times),
              product: num(product),
              n: fmt(report.n),
              gm: num(report.geometric),
            })}
          </Formula>
        ) : null}
        <Formula>
          {t(strings.harmonicLine, {
            n: fmt(report.n),
            sumReciprocal: num(report.sumReciprocal),
            hm: num(report.harmonic),
          })}
        </Formula>
        <Explanation>{strings.orderNote}</Explanation>
      </WorkingPanel>
    </>
  );
}

function GrowthResult({
  report,
  fmt,
  strings,
}: {
  report: GrowthReport;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const num = (value: number) => formatStat(fmt, value);
  const pct = (value: number) => formatPercent(fmt, value);
  const product = num(report.product);
  return (
    <>
      <div aria-live="polite">
        <StatGrid>
          <StatCard
            emphasis
            label={strings.averageRate}
            value={pct(report.averageRate)}
          />
          <StatCard
            label={strings.simpleAverage}
            value={pct(report.arithmeticRate)}
          />
          <StatCard
            label={strings.totalChange}
            value={pct(report.product - 1)}
          />
          <StatCard label={strings.periods} value={fmt(report.n)} />
        </StatGrid>
      </div>
      <WorkingPanel title={strings.workingTitle}>
        <Formula>
          {t(strings.factorsLine, {
            list: formatNumberList(
              fmt,
              report.factors,
              strings.listSeparator,
              strings.moreValues
            ),
          })}
        </Formula>
        <Formula>
          {t(strings.productFactorsLine, {
            product,
            total: pct(report.product - 1),
          })}
        </Formula>
        <Formula>
          {t(strings.averageLine, {
            product,
            n: fmt(report.n),
            factor: num(report.averageFactor),
            rate: pct(report.averageRate),
          })}
        </Formula>
        <Formula>
          {t(strings.checkLine, {
            factor: num(report.averageFactor),
            n: fmt(report.n),
            product,
          })}
        </Formula>
        <Explanation>{strings.growthNote}</Explanation>
      </WorkingPanel>
    </>
  );
}

function valuesProblem(
  values: number[],
  strings: Strings,
  locale: string,
  fmt: NumberFormatter
): string | null {
  const bad = values.filter((value) => !(value > 0)).length;
  if (bad > 0) {
    return tPlural(strings.nonPositive, locale, bad, { count: fmt(bad) });
  }
  return null;
}

export default function GeometricMeanCalculatorWidget() {
  const strings = useToolUi(GEOMETRIC_MEAN_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("values");
  const [valuesText, setValuesText] = useState("");
  const [changesText, setChangesText] = useState("");

  const isValues = mode === "values";
  // A typed "+10%" is read as 10: the % sign is dropped before the list is parsed.
  const values = parseNumberList(
    isValues ? valuesText : changesText.replaceAll("%", " ")
  );
  const problem = isValues ? valuesProblem(values, strings, locale, fmt) : null;
  const means = isValues && values.length >= 2 ? meansReport(values) : null;
  const growth = !isValues && values.length >= 1 ? growthReport(values) : null;
  const growthImpossible = !isValues && values.length >= 1 && growth === null;

  const field = isValues
    ? {
        label: strings.valuesLabel,
        placeholder: strings.valuesPlaceholder,
        value: valuesText,
        onChange: setValuesText,
        example: strings.exampleValues,
      }
    : {
        label: strings.changesLabel,
        placeholder: strings.changesPlaceholder,
        value: changesText,
        onChange: setChangesText,
        example: strings.exampleChanges,
      };

  return (
    <>
      <section className="space-y-4">
        <PillGroup
          label={strings.modeLabel}
          name="gm-mode"
          onChange={setMode}
          options={[
            { value: "values", label: strings.modeValues },
            { value: "growth", label: strings.modeGrowth },
          ]}
          value={mode}
        />
        <TextAreaField
          hint={
            <>
              {fmt(values.length)}
              {tPlural(strings.valuesDetected, locale, values.length)}
            </>
          }
          id="gm-data"
          label={field.label}
          onChange={field.onChange}
          placeholder={field.placeholder}
          value={field.value}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => field.onChange(field.example)}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => field.onChange("")}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
      {growthImpossible ? (
        <ErrorNotice>{strings.tooNegative}</ErrorNotice>
      ) : null}
      {means || growth ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          {means ? (
            <ValuesResult
              fmt={fmt}
              report={means}
              strings={strings}
              values={values}
            />
          ) : null}
          {growth ? (
            <GrowthResult fmt={fmt} report={growth} strings={strings} />
          ) : null}
        </section>
      ) : null}
      {means || growth || problem || growthImpossible ? null : (
        <Notice>{isValues ? strings.enterValues : strings.enterChanges}</Notice>
      )}
    </>
  );
}
